import { Component, Input } from '@angular/core';
import { NgClass, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

/**
 * SimpleBadgeComponent - Lightweight label/tag badge
 *
 * A simple, non-interactive badge for displaying metadata labels like
 * "Draft", "Coming Soon", "New", etc.
 *
 * ## Why separate from status-chip?
 *
 * | Feature              | simple-badge          | status-chip                    |
 * |----------------------|-----------------------|--------------------------------|
 * | **Purpose**          | Decorative labels     | Interactive status management  |
 * | **Interactivity**    | None                  | Click events, status selection |
 * | **Dependencies**     | None (pure CSS)       | Angular Material mat-chip      |
 * | **Loading states**   | No                    | Yes                            |
 * | **Status lookup**    | No                    | Yes (from statuses array)      |
 * | **Weight**           | Lightweight           | Heavier (Material overhead)    |
 *
 * Use simple-badge when you just need a quick visual label.
 * Use status-chip when users need to interact with or change statuses.
 *
 * ## When to migrate to status-chip
 *
 * If you find yourself needing to add interactivity to this badge (click
 * handlers, status changes, loading states), consider using app-status-chip
 * instead. It provides a more robust, dynamic chip component built on
 * Angular Material.
 *
 * @example
 * // Basic usage
 * <app-simple-badge label="Draft"></app-simple-badge>
 *
 * // With color variant
 * <app-simple-badge label="Published" color="success"></app-simple-badge>
 *
 * // Muted variant for "coming soon" type labels
 * <app-simple-badge label="Coming soon" color="muted"></app-simple-badge>
 *
 * // Small size for inline tags
 * <app-simple-badge label="AI Powered" size="sm"></app-simple-badge>
 */
@Component({
    selector: 'app-simple-badge',
    templateUrl: './simple-badge.component.html',
    styleUrls: ['./simple-badge.component.scss'],
    standalone: true,
    imports: [NgClass, NgIf, MatIconModule],
})
export class SimpleBadgeComponent {
    /**
     * The text to display in the badge
     */
    @Input({ required: true }) label: string;

    /**
     * Optional SVG icon to display before the label
     * Use the full icon name (e.g., 'icon-v2:ai-sparkle')
     */
    @Input() icon: string;

    /**
     * Color variant for the badge
     * - 'default': Gray background (default)
     * - 'muted': Slightly darker gray - for "Coming Soon" etc.
     * - 'primary': Brand primary color
     * - 'success': Green
     * - 'warning': Orange/amber
     * - 'danger': Red - for deleted items, errors, etc.
     * - 'outline': White bg with border - for use on gray backgrounds
     */
    @Input() color: 'default' | 'muted' | 'primary' | 'success' | 'warning' | 'danger' | 'outline' = 'default';

    /**
     * Size variant for the badge
     * - 'default': Standard size (11px font)
     * - 'sm': Smaller size (10px font, less padding) - for inline tags
     */
    @Input() size: 'default' | 'sm' = 'default';

    /**
     * Active/selected state for the badge
     * When true with 'outline' color, shows blue border and text
     */
    @Input() active = false;

    get badgeClass(): string {
        const classes: string[] = [];
        if (this.color !== 'default') {
            classes.push(`simple-badge--${this.color}`);
        }
        if (this.size === 'sm') {
            classes.push('simple-badge--sm');
        }
        if (this.active) {
            classes.push('simple-badge--active');
        }
        return classes.join(' ');
    }
}
